// DetailPanel — side drawer that slides in when a design card is clicked.
// Per spec §18.7. Shows hi-res preview, full metadata, order history, and
// action buttons: Reorder, Edit a copy, Pin, Move to folder, Rename,
// Archive, Delete.
//
// State is owned by the Zustand store (detailDesignId). The drawer is
// portal-less (rendered inline) but absolute-positioned so it overlays
// the view-area without disturbing layout.

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X,
  Pencil,
  ShoppingCart,
  Star,
  Folder as FolderIcon,
  Archive,
  Trash2,
  ImageIcon,
  RefreshCcw,
  Tag,
  Check,
} from 'lucide-react';
import {
  deleteDesign,
  forkDesign,
  getDesign,
  patchDesign,
  reorderDesign,
  restoreDesign,
  dispatchCartAdd,
  StudioApiError,
} from '../lib/api';
import { qk } from '../lib/queryClient';
import { useStudio } from '../lib/store';
import { fmtDate, fmtQty } from '../lib/format';
import { ConfirmDialog } from './ConfirmDialog';
import { FolderPicker } from './FolderPicker';
import { ActivityFeed } from './ActivityFeed';

const STATUS_LABELS = {
  draft: 'Draft',
  ordered: 'Ordered',
  in_production: 'In production',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
} as const;

export function DetailPanel() {
  const designId = useStudio((s) => s.detailDesignId);
  const close = useStudio((s) => s.closeDetail);
  // Ship 5c: variant chip nav uses openDetail to swap between siblings
  // without closing/reopening the panel. Subscribe via the hook so the
  // chip's onClick is consistent with the rest of this file's pattern.
  const openDetail = useStudio((s) => s.openDetail);
  const pushToast = useStudio((s) => s.pushToast);
  const qc = useQueryClient();

  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  // Ship 5 polish: collapse Details + Timeline by default. Most customers
  // never need created/updated dates or activity history; surfacing them
  // up front made the drawer feel cluttered. Click "Show more" to expand.
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  // Ship 5 polish: hover-to-preview a variant. Hovering any chip surfaces
  // a large thumbnail in a floating pane so the customer can read the
  // design before committing to navigate. Click still navigates as before.
  const [hoveredVariantId, setHoveredVariantId] = useState<string | null>(null);
  // Ship 6: bulk select within the variant strip. Independent of the
  // global bulkMode state on My Designs — selecting variants here doesn't
  // bleed into the grid. When 1+ chips are selected, an action bar appears
  // at the bottom of the strip with Archive + Move-to-folder.
  const [selectedVariantIds, setSelectedVariantIds] = useState<Set<string>>(() => new Set());
  function toggleVariantSelect(id: string) {
    setSelectedVariantIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function clearVariantSelection() {
    setSelectedVariantIds(new Set());
  }
  const [folderPickerOpen, setFolderPickerOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  const isOpen = designId !== null;

  // Fetch one design at a time. enabled=isOpen avoids a 404 query when
  // closed. staleTime=30s so re-opening the same drawer doesn't refetch
  // immediately.
  const designQ = useQuery({
    queryKey: qk.design(designId ?? ''),
    queryFn: () => getDesign(designId as string),
    enabled: isOpen,
    staleTime: 30_000,
  });
  const design = designQ.data?.design ?? null;
  // Ship 5c: family.variants — the personalized versions rolled up under
  // this design's root. Empty array if the design has no forks (or if the
  // server hasn't shipped Ship 5c yet — `family` is optional on the wire).
  const family = designQ.data?.family ?? null;
  const variants = family?.variants ?? [];
  const isVariant = !!design?.forked_from;

  // Esc to close (but not if a sub-confirm or sub-picker is open — those
  // own the keyboard while they're up).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (confirmDelete || confirmArchive || folderPickerOpen || renaming) return;
      close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, close, confirmDelete, confirmArchive, folderPickerOpen, renaming]);

  // View tracking — when the panel opens for an unviewed design, stamp
  // viewed_at so the "New custom design from Market Dwellings" banner
  // dismisses for this design once they've actually looked at it. Only
  // fires when source_kind is 'attached' and viewed_at is null; cheap
  // no-op for organic/forked designs.
  useEffect(() => {
    if (!design) return;
    if (design.source_kind !== 'attached') return;
    if (design.viewed_at) return;
    void import('../lib/api').then(({ markDesignViewed }) => {
      markDesignViewed(design.id).then(() => {
        void qc.invalidateQueries({ queryKey: qk.designs() });
      }).catch(() => {
        // Non-blocking — if the stamp fails the user will just see the
        // banner again next refresh, which is fine.
      });
    });
  }, [design, qc]);

  // ── Mutations ───────────────────────────────────────────────────────────
  const reorderMut = useMutation({
    mutationFn: async () => {
      if (!design) throw new Error('no design');
      const desc = await reorderDesign(design.id);
      return dispatchCartAdd(desc);
    },
    onSuccess: (cartUrl) => {
      window.location.href = cartUrl;
    },
    onError: (err) =>
      pushToast({
        type: 'error',
        message: err instanceof StudioApiError ? err.message : 'Could not add to cart',
      }),
  });

  const forkMut = useMutation({
    mutationFn: async () => (design ? forkDesign(design.id) : Promise.reject()),
    onSuccess: (resp) => {
      window.location.href = resp.editor_url;
    },
    onError: (err) =>
      pushToast({
        type: 'error',
        message: err instanceof StudioApiError ? err.message : 'Could not fork design',
      }),
  });

  const pinMut = useMutation({
    mutationFn: async (pinned: boolean) =>
      design ? patchDesign(design.id, { is_pinned: pinned }) : Promise.reject(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['design', design?.id] });
      void qc.invalidateQueries({ queryKey: ['designs'] });
    },
  });

  const renameMut = useMutation({
    mutationFn: async (name: string | null) =>
      design ? patchDesign(design.id, { custom_name: name }) : Promise.reject(),
    onSuccess: () => {
      setRenaming(false);
      pushToast({ type: 'success', message: 'Name updated' });
      void qc.invalidateQueries({ queryKey: ['design', design?.id] });
      void qc.invalidateQueries({ queryKey: ['designs'] });
    },
    onError: (err) =>
      pushToast({
        type: 'error',
        message: err instanceof StudioApiError ? err.message : 'Could not update name',
      }),
  });

  const archiveMut = useMutation({
    mutationFn: async () =>
      design ? patchDesign(design.id, { archived: !design.archived_at }) : Promise.reject(),
    onSuccess: () => {
      setConfirmArchive(false);
      pushToast({
        type: 'success',
        message: design?.archived_at ? 'Restored' : 'Archived',
      });
      void qc.invalidateQueries({ queryKey: ['design', design?.id] });
      void qc.invalidateQueries({ queryKey: ['designs'] });
      // Closing on archive feels right — the design just left the active grid.
      if (!design?.archived_at) close();
    },
  });

  const restoreMut = useMutation({
    mutationFn: async () => (design ? restoreDesign(design.id) : Promise.reject()),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Restored' });
      void qc.invalidateQueries({ queryKey: ['design', design?.id] });
      void qc.invalidateQueries({ queryKey: ['designs'] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async () => (design ? deleteDesign(design.id) : Promise.reject()),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Design moved to trash' });
      void qc.invalidateQueries({ queryKey: ['designs'] });
      void qc.invalidateQueries({ queryKey: qk.folders });
      close();
    },
    onError: (err) =>
      pushToast({
        type: 'error',
        message: err instanceof StudioApiError ? err.message : 'Could not delete design',
      }),
  });

  if (!isOpen) return null;

  const isArchived = !!design?.archived_at;
  // "Reorder" implies prior purchase. Drafts (never ordered) get the
  // honest "Order" label on the same primary CTA — same backend call.
  const isDraft = design?.status === 'draft';

  return (
    <>
      <div
        className="detail-backdrop"
        role="presentation"
        onClick={() => {
          if (!confirmDelete && !confirmArchive && !folderPickerOpen) close();
        }}
      />
      <aside
        ref={drawerRef}
        className="detail-panel"
        role="dialog"
        aria-modal="false"
        aria-label="Design details"
      >
        <header className="detail-panel__head">
          <h3>Design details</h3>
          <button
            type="button"
            className="detail-panel__close"
            onClick={close}
            aria-label="Close design details"
          >
            <X size={18} strokeWidth={1.8} />
          </button>
        </header>

        <div className="detail-panel__body">
          {designQ.isLoading || !design ? (
            <div className="detail-panel__loading">Loading…</div>
          ) : (
            <>
              {/* Hi-res preview */}
              <div className="detail-panel__preview">
                {design.thumbnail.url ? (
                  <img
                    src={design.thumbnail.url}
                    alt={`Preview of ${design.name ?? 'design'}`}
                    loading="lazy"
                  />
                ) : (
                  <div className="detail-panel__preview-empty">
                    <ImageIcon size={40} strokeWidth={1.4} />
                    <span>No preview yet</span>
                  </div>
                )}
              </div>

              {/* Title + rename */}
              <div className="detail-panel__title-block">
                {renaming ? (
                  <form
                    className="detail-panel__rename"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const trimmed = draftName.trim();
                      renameMut.mutate(trimmed || null);
                    }}
                  >
                    <input
                      type="text"
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      maxLength={160}
                      autoFocus
                      placeholder={design.shopify.product_title ?? 'Untitled design'}
                      aria-label="Rename design"
                    />
                    <button
                      type="submit"
                      className="btn btn--primary btn--sm"
                      disabled={renameMut.isPending}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setRenaming(false)}
                    >
                      Cancel
                    </button>
                  </form>
                ) : (
                  <div className="detail-panel__title-row">
                    <h2 className="detail-panel__title">
                      {design.name ?? 'Untitled design'}
                    </h2>
                    <button
                      type="button"
                      className="detail-panel__icon-btn"
                      aria-label="Rename"
                      onClick={() => {
                        setDraftName(design.custom_name ?? '');
                        setRenaming(true);
                      }}
                    >
                      <Pencil size={16} strokeWidth={1.6} />
                    </button>
                  </div>
                )}
                <p className="detail-panel__sub">
                  <span className={`pill pill--${design.status}`}>
                    {STATUS_LABELS[design.status]}
                  </span>
                  {design.shopify.product_title && (
                    <>
                      <span aria-hidden="true"> · </span>
                      {design.shopify.product_title}
                    </>
                  )}
                </p>
              </div>

              {/* Primary actions */}
              <div className="detail-panel__actions">
                {isArchived ? (
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => restoreMut.mutate()}
                    disabled={restoreMut.isPending}
                  >
                    <RefreshCcw size={16} strokeWidth={1.6} aria-hidden="true" />
                    <span>Restore</span>
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn btn--hero"
                      onClick={() => reorderMut.mutate()}
                      disabled={reorderMut.isPending}
                    >
                      <ShoppingCart size={16} strokeWidth={1.6} aria-hidden="true" />
                      <span>{isDraft ? 'Order' : 'Reorder'}</span>
                    </button>
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => forkMut.mutate()}
                      disabled={forkMut.isPending}
                    >
                      <Pencil size={16} strokeWidth={1.6} aria-hidden="true" />
                      <span>Edit a copy</span>
                    </button>
                  </>
                )}
              </div>

              {/* Quick toggles */}
              <div className="detail-panel__quick">
                <button
                  type="button"
                  className={`detail-panel__quick-btn${design.is_pinned ? ' is-on' : ''}`}
                  onClick={() => pinMut.mutate(!design.is_pinned)}
                >
                  <Star
                    size={16}
                    strokeWidth={1.6}
                    fill={design.is_pinned ? 'currentColor' : 'none'}
                    aria-hidden="true"
                  />
                  <span>{design.is_pinned ? 'Pinned' : 'Pin'}</span>
                </button>
                <button
                  type="button"
                  className="detail-panel__quick-btn"
                  onClick={() => setFolderPickerOpen(true)}
                >
                  <FolderIcon size={16} strokeWidth={1.6} aria-hidden="true" />
                  <span>Move to folder</span>
                </button>
                {!isArchived && (
                  <button
                    type="button"
                    className="detail-panel__quick-btn"
                    onClick={() => setConfirmArchive(true)}
                  >
                    <Archive size={16} strokeWidth={1.6} aria-hidden="true" />
                    <span>Archive</span>
                  </button>
                )}
                {/* Ship 6: Delete only available in the Archived view, and
                    only for forks (master protection). The button is hidden
                    rather than disabled-with-tooltip — agents told us a
                    greyed-out button feels confusing. Quietly absent is
                    cleaner. Masters always get the archive flow instead
                    (which they already saw if they wanted to remove). */}
                {isArchived && design.source_kind === 'forked' && (
                  <button
                    type="button"
                    className="detail-panel__quick-btn detail-panel__quick-btn--danger"
                    onClick={() => setConfirmDelete(true)}
                  >
                    <Trash2 size={16} strokeWidth={1.6} aria-hidden="true" />
                    <span>Delete permanently</span>
                  </button>
                )}
              </div>

              {/* Ship 5c: rolled-up variants strip. Surfaces personalized
                  versions of this design's root master so the customer can
                  jump between Sarah's version → Carter's version → master
                  without leaving the drawer. Hidden when there are no
                  variants (most cards) so the drawer stays clean. When the
                  drawer was opened on a fork rather than its root, we offer
                  a "Back to master" affordance so the customer can navigate
                  up the family tree without going back to the grid first. */}
              {(variants.length > 0 || isVariant) && family && (() => {
                // Resolve which design is currently being previewed: the
                // hovered variant takes precedence, otherwise show whichever
                // chip is "current." This lets the customer scan through
                // variants by hovering chips one at a time and seeing each
                // design at readable size before committing to navigate.
                const previewable: Array<{ id: string; thumb: string | null; name: string; label: string }> = [
                  {
                    id: family.root.id,
                    thumb: family.root.thumbnail.url,
                    name: family.root.custom_name ?? family.root.shopify.product_title ?? 'Master',
                    label: 'Master',
                  },
                  ...variants.map((v) => ({
                    id: v.id,
                    thumb: v.thumbnail.url,
                    name: v.custom_name ?? 'Untitled version',
                    label: v.status === 'draft' ? 'Draft' : 'Ordered',
                  })),
                ];
                const previewing = previewable.find((p) => p.id === hoveredVariantId)
                  ?? previewable.find((p) => p.id === design.id)
                  ?? previewable[0];
                return (
                  <section className="detail-panel__section detail-panel__variants">
                    <h4 className="detail-panel__section-title">
                      {variants.length === 0
                        ? 'This is a personalized version'
                        : `Personalized versions · ${variants.length}`}
                    </h4>

                    {/* Hover-preview pane: a single large thumbnail that
                        updates based on which chip is being hovered (or
                        defaults to the current design). Lets the customer
                        actually SEE the design before clicking. */}
                    {previewing && (
                      <div
                        className="variant-preview-pane"
                        aria-live="polite"
                      >
                        {previewing.thumb ? (
                          <img
                            src={previewing.thumb}
                            alt={`Preview of ${previewing.name}`}
                            loading="lazy"
                          />
                        ) : (
                          <div className="variant-preview-pane__empty">
                            <ImageIcon size={32} strokeWidth={1.2} />
                            <span>No preview yet</span>
                          </div>
                        )}
                        <div className="variant-preview-pane__caption">
                          <span className="variant-preview-pane__label">{previewing.label}</span>
                          <span className="variant-preview-pane__name">{previewing.name}</span>
                          {hoveredVariantId && hoveredVariantId !== design.id && (
                            <span className="variant-preview-pane__hint">Click chip to switch</span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className={`variant-strip${selectedVariantIds.size > 0 ? ' has-selection' : ''}`}>
                      {/* Always show the root as the first chip. Highlighted
                          when the user is currently looking at it. */}
                      <ChipWithCheckbox
                        chip={{
                          id: family.root.id,
                          isCurrent: family.root.id === design.id,
                          isSelected: selectedVariantIds.has(family.root.id),
                          thumb: family.root.thumbnail.url,
                          label: 'Master',
                          name: family.root.custom_name ?? family.root.shopify.product_title ?? 'Master',
                        }}
                        onNavigate={() => {
                          if (family.root.id !== design.id) openDetail(family.root.id);
                        }}
                        onToggleSelect={() => toggleVariantSelect(family.root.id)}
                        onHoverEnter={() => setHoveredVariantId(family.root.id)}
                        onHoverLeave={() => setHoveredVariantId(null)}
                      />
                      {variants.map((v) => (
                        <ChipWithCheckbox
                          key={v.id}
                          chip={{
                            id: v.id,
                            isCurrent: v.id === design.id,
                            isSelected: selectedVariantIds.has(v.id),
                            thumb: v.thumbnail.url,
                            label: v.status === 'draft' ? 'Draft' : 'Ordered',
                            name: v.custom_name ?? 'Untitled version',
                          }}
                          onNavigate={() => {
                            if (v.id !== design.id) openDetail(v.id);
                          }}
                          onToggleSelect={() => toggleVariantSelect(v.id)}
                          onHoverEnter={() => setHoveredVariantId(v.id)}
                          onHoverLeave={() => setHoveredVariantId(null)}
                        />
                      ))}
                    </div>

                    {/* Bulk action bar — only renders when 1+ chips are
                        selected. Archive + Move to folder. No Delete in
                        the main view per the Ship 6 scope (Delete lives in
                        the Archived view only). Clear-selection link on
                        the right resets without applying anything. */}
                    {selectedVariantIds.size > 0 && (
                      <VariantBulkActions
                        selectedIds={Array.from(selectedVariantIds)}
                        onClear={clearVariantSelection}
                      />
                    )}
                  </section>
                );
              })()}

              {/* Order history rollups — only shown for designs that have
                  actually been ordered. Drafts (total_orders=0) hide this
                  section entirely; surfacing "0 orders" felt like noise on
                  the My Designs drawer. */}
              {design.rollups.total_orders > 0 && (
                <section className="detail-panel__section">
                  <h4 className="detail-panel__section-title">Order history</h4>
                  <dl className="detail-panel__stats">
                    <div>
                      <dt>Times ordered</dt>
                      <dd>{design.rollups.total_orders}</dd>
                    </div>
                    <div>
                      <dt>Total quantity</dt>
                      <dd>{fmtQty(design.rollups.total_qty)}</dd>
                    </div>
                    <div>
                      <dt>Last ordered</dt>
                      <dd>{fmtDate(design.rollups.last_ordered_at)}</dd>
                    </div>
                    {design.rollups.last_ordered_qty != null && (
                      <div>
                        <dt>Last quantity</dt>
                        <dd>{fmtQty(design.rollups.last_ordered_qty)}</dd>
                      </div>
                    )}
                    {design.rollups.last_ordered_order_name && (
                      <div>
                        <dt>Order #</dt>
                        <dd>
                          <a
                            href={`https://${design.shopify.shop_domain}/account/orders/${design.rollups.last_ordered_order_id ?? ''}`}
                            target="_top"
                            rel="noreferrer"
                          >
                            {design.rollups.last_ordered_order_name}
                          </a>
                        </dd>
                      </div>
                    )}
                  </dl>
                </section>
              )}

              {/* Assigned team members (Ship 3). */}
              <DetailPanelAgents designId={design.id} />

              {/* Show more / show less toggle for the metadata + timeline.
                  Most customers never need to see the created/updated/saved
                  dates or the activity log — collapsing them by default
                  makes the drawer feel calmer. The toggle text reflects
                  whether anything is currently visible underneath. */}
              <div className="detail-panel__more-toggle">
                <button
                  type="button"
                  className="detail-panel__more-btn"
                  onClick={() => setShowMoreDetails((v) => !v)}
                  aria-expanded={showMoreDetails}
                >
                  {showMoreDetails ? 'Show less' : 'Show more details'}
                </button>
              </div>

              {showMoreDetails && (
                <>
                  {/* Metadata */}
                  <section className="detail-panel__section">
                    <h4 className="detail-panel__section-title">Details</h4>
                    <dl className="detail-panel__stats">
                      <div>
                        <dt>Created</dt>
                        <dd>{fmtDate(design.created_at)}</dd>
                      </div>
                      <div>
                        <dt>Last updated</dt>
                        <dd>{fmtDate(design.updated_at)}</dd>
                      </div>
                      {design.thumbnail.snapshotted_at && (
                        <div>
                          <dt>Preview saved</dt>
                          <dd>{fmtDate(design.thumbnail.snapshotted_at)}</dd>
                        </div>
                      )}
                      {design.shopify.product_type && (
                        <div>
                          <dt>Product type</dt>
                          <dd>
                            <Tag size={12} strokeWidth={1.6} aria-hidden="true" /> {design.shopify.product_type}
                          </dd>
                        </div>
                      )}
                      {design.forked_from && (
                        <div>
                          <dt>Forked from</dt>
                          <dd>
                            <button
                              type="button"
                              className="link"
                              onClick={() => openDetail(design.forked_from as string)}
                            >
                              Open original →
                            </button>
                          </dd>
                        </div>
                      )}
                    </dl>
                  </section>

                  {/* Per-design activity timeline */}
                  <section className="detail-panel__section">
                    <h4 className="detail-panel__section-title">Timeline</h4>
                    <ActivityFeed limit={20} designId={design.id} compact />
                  </section>
                </>
              )}
            </>
          )}
        </div>
      </aside>

      {/* Sub-dialogs */}
      {/* Ship 5d: master-aware delete confirmation. For forked variants
          (the "Edit a copy" disposables), keep the existing delete copy.
          For attached/organic designs, the server rejects hard-delete with
          a 409, so the dialog instead steers to Archive. Same button click,
          different mutation — feels seamless to the customer.

          Guarded on `design` being non-null so the dialog never accesses
          fields on a still-loading or freshly-closed panel. confirmDelete
          can only be true after the user clicks Delete, which is itself
          inside the design-loaded branch — but defensive anyway. */}
      {design && (design.source_kind === 'forked' ? (
        <ConfirmDialog
          open={confirmDelete}
          title="Delete this version?"
          body={
            <p>
              This personalized version will move to your Archived folder.
              You can restore it within 90 days; after that it&rsquo;s permanently deleted.
              The master design stays in your library either way.
            </p>
          }
          confirmLabel="Delete"
          destructive
          busy={deleteMut.isPending}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => deleteMut.mutate()}
        />
      ) : (
        <ConfirmDialog
          open={confirmDelete}
          title="Move this design to your archive?"
          body={
            <>
              <p>
                It&rsquo;ll disappear from your active library and live in the Archived view —
                one click to restore whenever you need it.
              </p>
              <p style={{ marginTop: 8, fontSize: '0.88rem', color: 'var(--ink-soft)' }}>
                Designs from Market Dwellings stay protected — they&rsquo;re always one click
                away if you change your mind.
              </p>
            </>
          }
          confirmLabel="Archive"
          busy={archiveMut.isPending}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => {
            // Fall through to archive flow rather than delete. Closes the
            // confirm dialog after the archiveMut settles via its onSuccess
            // (which already handles closing the panel for active archives).
            setConfirmDelete(false);
            if (!design.archived_at) archiveMut.mutate();
          }}
        />
      ))}

      <ConfirmDialog
        open={confirmArchive}
        title="Archive this design?"
        body={
          <p>
            Archived designs are hidden from your main library but stay accessible from the
            Archived folder.
          </p>
        }
        confirmLabel="Archive"
        busy={archiveMut.isPending}
        onCancel={() => setConfirmArchive(false)}
        onConfirm={() => archiveMut.mutate()}
      />

      {design && (
        <FolderPicker
          open={folderPickerOpen}
          mode="move"
          designIds={[design.id]}
          onClose={() => setFolderPickerOpen(false)}
        />
      )}
    </>
  );
}

/** Assigned team-member chips for the open design + an "Add member"
 *  picker. Clicking the X on a chip unassigns. */
// Ship 6: ChipWithCheckbox — wraps a single variant chip with the new
// always-visible select checkbox. The checkbox sits in the top-left
// corner at low opacity, full opacity on chip hover or when selected.
// Click checkbox = toggle selection (without navigating). Click anywhere
// else on the chip = navigate to that variant (existing behavior).
interface ChipWithCheckboxProps {
  chip: {
    id: string;
    isCurrent: boolean;
    isSelected: boolean;
    thumb: string | null;
    label: string;
    name: string;
  };
  onNavigate: () => void;
  onToggleSelect: () => void;
  onHoverEnter: () => void;
  onHoverLeave: () => void;
}

function ChipWithCheckbox({
  chip,
  onNavigate,
  onToggleSelect,
  onHoverEnter,
  onHoverLeave,
}: ChipWithCheckboxProps) {
  return (
    <div
      className={`variant-chip${chip.isCurrent ? ' is-current' : ''}${chip.isSelected ? ' is-selected' : ''}`}
      onMouseEnter={onHoverEnter}
      onMouseLeave={onHoverLeave}
    >
      <button
        type="button"
        className={`variant-chip__select${chip.isSelected ? ' is-on' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          onToggleSelect();
        }}
        aria-label={chip.isSelected ? 'Deselect' : 'Select'}
        aria-pressed={chip.isSelected}
      >
        {chip.isSelected ? <Check size={11} strokeWidth={2.6} /> : null}
      </button>
      <button
        type="button"
        className="variant-chip__hit"
        onClick={() => {
          if (!chip.isCurrent) onNavigate();
        }}
        onFocus={onHoverEnter}
        onBlur={onHoverLeave}
        aria-current={chip.isCurrent}
      >
        <span className="variant-chip__thumb" aria-hidden="true">
          {chip.thumb ? (
            <img src={chip.thumb} alt="" loading="lazy" />
          ) : (
            <ImageIcon size={18} strokeWidth={1.4} />
          )}
        </span>
        <span className="variant-chip__meta">
          <span className="variant-chip__label">{chip.label}</span>
          <span className="variant-chip__name">{chip.name}</span>
        </span>
      </button>
    </div>
  );
}

// Ship 6: VariantBulkActions — appears below the variant grid when 1+
// chips are selected. Archive + Move-to-folder are the only bulk actions
// in the main view (Delete lives only in the Archived view per spec).
// Selection state lives in the parent so both the chips and the bar
// stay in sync.
function VariantBulkActions({
  selectedIds,
  onClear,
}: {
  selectedIds: string[];
  onClear: () => void;
}) {
  const qc = useQueryClient();
  const pushToast = useStudio((s) => s.pushToast);
  const [folderPickerOpen, setFolderPickerOpenLocal] = useState(false);
  const [confirmBulkArchive, setConfirmBulkArchive] = useState(false);

  const archiveMut = useMutation({
    mutationFn: async () => {
      const api = await import('../lib/api');
      return api.bulkArchive(selectedIds);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['designs'] });
      void qc.invalidateQueries({ queryKey: qk.folders });
      const n = selectedIds.length;
      pushToast({
        type: 'success',
        message: `Archived ${n} version${n === 1 ? '' : 's'}`,
        // Ship 6: Undo extends Ship 1's bulk-undo pattern. The toast
        // surfaces a 15s window to bulk-restore. Re-archiving via the same
        // endpoint isn't undo-friendly; use the bulk restore endpoint if
        // your codebase has one, otherwise iterate via PATCH.
        action: {
          label: 'Undo',
          onClick: () => {
            void Promise.all(
              selectedIds.map(async (id) => {
                const api = await import('../lib/api');
                return api.patchDesign(id, { archived: false });
              }),
            ).then(() => {
              void qc.invalidateQueries({ queryKey: ['designs'] });
              pushToast({ type: 'success', message: 'Restored' });
            });
          },
        },
      });
      onClear();
      setConfirmBulkArchive(false);
    },
    onError: () =>
      pushToast({ type: 'error', message: 'Could not archive' }),
  });

  const moveMut = useMutation({
    mutationFn: async (folderId: string | null) => {
      const api = await import('../lib/api');
      return api.bulkMove(selectedIds, folderId);
    },
    onSuccess: (_resp, folderId) => {
      void qc.invalidateQueries({ queryKey: ['designs'] });
      void qc.invalidateQueries({ queryKey: qk.folders });
      pushToast({
        type: 'success',
        message: folderId
          ? `Moved ${selectedIds.length} to folder`
          : `Removed ${selectedIds.length} from folder`,
      });
      onClear();
      setFolderPickerOpenLocal(false);
    },
    onError: () =>
      pushToast({ type: 'error', message: 'Could not move' }),
  });

  const n = selectedIds.length;

  return (
    <>
      <div className="variant-bulk-bar" role="toolbar" aria-label="Bulk actions">
        <span className="variant-bulk-bar__count">
          {n} selected
        </span>
        <div className="variant-bulk-bar__actions">
          <button
            type="button"
            className="btn btn--small"
            onClick={() => {
              // Ship 6: confirm-before-archive when 5+ selected so a
              // misclick on a packed strip can't sweep up a customer's
              // whole family. Single-digit selections skip the dialog
              // since the action is reversible via Undo anyway.
              if (n >= 5) setConfirmBulkArchive(true);
              else archiveMut.mutate();
            }}
            disabled={archiveMut.isPending}
          >
            <Archive size={13} strokeWidth={1.6} aria-hidden="true" />
            Archive
          </button>
          <button
            type="button"
            className="btn btn--small"
            onClick={() => setFolderPickerOpenLocal(true)}
            disabled={moveMut.isPending}
          >
            <FolderIcon size={13} strokeWidth={1.6} aria-hidden="true" />
            Move to folder
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={onClear}
          >
            Clear
          </button>
        </div>
      </div>

      <FolderPicker
        open={folderPickerOpen}
        mode="move"
        designIds={selectedIds}
        onClose={() => setFolderPickerOpenLocal(false)}
        onMoved={(folderId) => moveMut.mutate(folderId)}
      />

      <ConfirmDialog
        open={confirmBulkArchive}
        title={`Archive ${n} version${n === 1 ? '' : 's'}?`}
        body={
          <p>
            They&rsquo;ll move to your Archived view and stop appearing in this drawer.
            You can restore any of them with one click.
          </p>
        }
        confirmLabel="Archive all"
        busy={archiveMut.isPending}
        onCancel={() => setConfirmBulkArchive(false)}
        onConfirm={() => archiveMut.mutate()}
      />
    </>
  );
}

function DetailPanelAgents({ designId }: { designId: string }) {
  const qc = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const pushToast = useStudio((s) => s.pushToast);

  const query = useQuery({
    queryKey: ['design-agents', designId],
    queryFn: async () => (await import('../lib/api')).listDesignAgents(designId),
  });

  const assignMut = useMutation({
    mutationFn: async (agentIds: string[]) => {
      const api = await import('../lib/api');
      return api.assignAgentsToDesign(designId, agentIds);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['design-agents', designId] });
      void qc.invalidateQueries({ queryKey: ['designs'] });
      setPickerOpen(false);
      pushToast({ type: 'success', message: 'Assigned' });
    },
    onError: () => pushToast({ type: 'error', message: 'Could not assign' }),
  });

  const unassignMut = useMutation({
    mutationFn: async (agentId: string) => {
      const api = await import('../lib/api');
      return api.unassignAgentFromDesign(designId, agentId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['design-agents', designId] });
      void qc.invalidateQueries({ queryKey: ['designs'] });
    },
    onError: () => pushToast({ type: 'error', message: 'Could not remove' }),
  });

  const agents = query.data?.agents ?? [];

  // Ship 5 polish: when nothing's assigned, render a quiet inline link
  // instead of a full section with title + empty-state copy. Keeps the
  // drawer feeling calm for the common case (no team / no assignment).
  // Once an agent's assigned, full section renders.
  if (agents.length === 0) {
    return (
      <>
        <div className="detail-panel__assign-quiet">
          <button
            type="button"
            className="detail-panel__assign-link"
            onClick={() => setPickerOpen(true)}
          >
            + Assign to a team member
          </button>
        </div>
        <AgentPickerLazy
          open={pickerOpen}
          initialSelectedIds={[]}
          title="Assign team members to this design"
          confirmLabel="Save"
          onClose={() => setPickerOpen(false)}
          onConfirm={(ids) => assignMut.mutate(ids)}
          busy={assignMut.isPending}
        />
      </>
    );
  }

  return (
    <section className="detail-panel__section">
      <h4 className="detail-panel__section-title">Team members</h4>
      <ul className="detail-panel__agent-chips">
        {agents.map((a) => (
          <li key={a.id} className="detail-panel__agent-chip">
            {a.headshot_url ? (
              <img src={a.headshot_url} alt="" className="detail-panel__agent-avatar" />
            ) : (
              <span className="detail-panel__agent-avatar detail-panel__agent-avatar--placeholder">
                {a.first_name.charAt(0)}
              </span>
            )}
            <span>
              {a.first_name}
              {a.last_name ? ` ${a.last_name}` : ''}
            </span>
            <button
              type="button"
              className="detail-panel__agent-remove"
              aria-label={`Remove ${a.first_name}`}
              onClick={() => unassignMut.mutate(a.id)}
              disabled={unassignMut.isPending}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="btn btn--ghost btn--small"
        onClick={() => setPickerOpen(true)}
      >
        + Add team member
      </button>

      <AgentPickerLazy
        open={pickerOpen}
        initialSelectedIds={agents.map((a) => a.id)}
        title="Assign team members to this design"
        confirmLabel="Save"
        onClose={() => setPickerOpen(false)}
        onConfirm={(ids) => assignMut.mutate(ids)}
        busy={assignMut.isPending}
      />
    </section>
  );
}

/** Lazy import wrapper so DetailPanel doesn't pull AgentPicker on every
 *  page load — only when a design is opened. */
function AgentPickerLazy(
  props: Parameters<typeof import('./AgentPicker').AgentPicker>[0],
) {
  const [Comp, setComp] = useState<typeof import('./AgentPicker').AgentPicker | null>(null);
  useEffect(() => {
    if (props.open && !Comp) {
      void import('./AgentPicker').then((m) => setComp(() => m.AgentPicker));
    }
  }, [props.open, Comp]);
  if (!props.open || !Comp) return null;
  return <Comp {...props} />;
}
